import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouter } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { ApiError, type AdminApi, type EmployeeSummary, type GrantSummary, type NewGrant } from '../../api/client.ts';
import { environmentsQuery, meQuery } from '../../api/queries.ts';
import { usePermissions } from './usePermissions.ts';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

/** Roles and individual permissions of one employee, per organization / workspace / environment (BB-15). */
export function GrantsPanel({ employee }: { employee: EmployeeSummary }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const { environmentId } = useParams({ strict: false }) as { environmentId?: string };
  const queryClient = useQueryClient();
  const org = usePermissions('organization');
  const grants = useQuery({ queryKey: ['grants', employee.id], queryFn: () => api.listGrants(employee.id) });
  const catalog = useQuery({ queryKey: ['access-catalog'], queryFn: () => api.accessCatalog() });
  const environments = useQuery(environmentsQuery(api));
  const me = useQuery(meQuery(api));

  const [kind, setKind] = useState<'role' | 'permission'>('role');
  const [value, setValue] = useState('');
  const [scope, setScope] = useState('');

  const refresh = () =>
    Promise.all(
      [['grants', employee.id], ['environments'], ['me']].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    );
  const add = useMutation({ mutationFn: (grant: NewGrant) => api.addGrant(employee.id, grant), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (grantId: string) => api.removeGrant(employee.id, grantId), onSuccess: refresh });

  const organizationId = me.data?.employee.organizationId;
  const current = environments.data?.find((env) => env.id === environmentId);
  const scopes = [
    ...(organizationId ? [{ key: `organization:${organizationId}`, label: 'Organization' }] : []),
    ...(current ? [{ key: `workspace:${current.workspaceId}`, label: 'Workspace (all its environments)' }] : []),
    ...(environments.data ?? []).map((env) => ({ key: `environment:${env.id}`, label: `Environment: ${env.name}` })),
  ];
  const scopeLabel = (grant: GrantSummary) =>
    grant.scopeType === 'organization'
      ? 'Organization'
      : grant.scopeType === 'workspace'
        ? 'Workspace'
        : `Environment: ${environments.data?.find((env) => env.id === grant.scopeId)?.name ?? grant.scopeId}`;

  const special = new Set(catalog.data?.specialPermissions ?? []);
  const roleOptions = (catalog.data?.roles ?? []).map((r) => r.role).filter((role) => role !== 'owner' || org.has('org.manage'));
  const options = kind === 'role' ? roleOptions : (catalog.data?.permissions ?? []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const [scopeType, scopeId] = (scope || scopes[0]?.key || '').split(':') as [NewGrant['scopeType'], string];
    const chosen = value || options[0];
    if (!chosen || !scopeId) return;
    add.mutate(kind === 'role' ? { kind, role: chosen, scopeType, scopeId } : { kind, permission: chosen, scopeType, scopeId });
  };

  return (
    <section className="grants" aria-label={`Grants for ${employee.email}`}>
      <h2>Grants for {employee.email}</h2>
      {grants.isPending ? (
        <p aria-busy="true">Loading grants…</p>
      ) : grants.isError ? (
        <p role="alert">Grants could not be loaded.</p>
      ) : grants.data.length === 0 ? (
        <p className="empty-state">No grants yet: this person can sign in but sees nothing.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Grant</th>
              <th scope="col">Scope</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {grants.data.map((grant) => {
              const name = grant.role ?? grant.permission ?? '';
              return (
                <tr key={grant.id}>
                  <td>
                    {grant.kind === 'role' ? `Role: ${name}` : <code>{name}</code>}
                    {grant.permission && special.has(grant.permission) && <span className="tag">sensitive</span>}
                  </td>
                  <td>{scopeLabel(grant)}</td>
                  <td>
                    <button type="button" onClick={() => remove.mutate(grant.id)} disabled={remove.isPending} aria-label={`Remove ${name}`}>
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {remove.isError && <p role="alert">{errorText(remove.error)}</p>}

      <form className="inline-form" onSubmit={submit} aria-label="Add grant">
        <select aria-label="Grant kind" value={kind} onChange={(e) => (setKind(e.target.value as 'role' | 'permission'), setValue(''))}>
          <option value="role">Role</option>
          <option value="permission">Permission</option>
        </select>
        <select aria-label={kind === 'role' ? 'Role' : 'Permission'} value={value || options[0] || ''} onChange={(e) => setValue(e.target.value)}>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
              {special.has(option) ? ' (sensitive)' : ''}
            </option>
          ))}
        </select>
        <select aria-label="Scope" value={scope || scopes[0]?.key || ''} onChange={(e) => setScope(e.target.value)}>
          {scopes.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
        <button type="submit" disabled={add.isPending || !catalog.data}>
          Add grant
        </button>
        {add.isError && <span role="alert">{errorText(add.error)}</span>}
      </form>
    </section>
  );
}
